import { AuthService } from 'src/app/auth/services/auth.service';
import { Injectable, inject } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { ApiConfig } from 'src/api.config';
import { Router } from '@angular/router';
import { catchError, switchMap } from 'rxjs/operators';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { INTERCEPTOR } from '../../constants';
import { TranslateService } from '@ngx-translate/core';

const LANGUAGE_MAP: Record<string, string> = {
  kz: 'kk-KZ',
  en: 'en-GB',
};

const DEFAULT_LANGUAGE = 'ru-RU';
const BYPASS_PATTERNS = [ApiConfig.authApi + '/token', '/assets/i18n/', 'api.pexels.com'];
export const SKIP_GLOBAL_ERROR = new HttpContextToken<boolean>(() => false);

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly ntf = inject(NotificationService);
  private readonly authService = inject(AuthService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    request = this.addLanguageHeader(request);

    if (request.url.includes(ApiConfig.mainApiPublic) || this.shouldBypass(request.url)) {
      return next.handle(request);
    }

    const token = this.authService.getAuthToken();

    if (this.authService.isExpired(60) && token?.refresh_token) {
      return this.authService.refreshToken().pipe(
        switchMap(() => next.handle(this.addAuthHeader(request))),
        catchError((error) => {
          this.authService.clearToken();
          this.authService.navigateToSignIn(this.authService.resolveSignInRedirectUrl());
          return throwError(() => error);
        })
      );
    }

    return next.handle(this.addAuthHeader(request)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!request.context.get(SKIP_GLOBAL_ERROR)) {
          this.handleHttpError(error);
        }
        return throwError(() => error);
      })
    );
  }

  private shouldBypass(url: string): boolean {
    return BYPASS_PATTERNS.some((pattern) => url.includes(pattern));
  }

  private addAuthHeader(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      return request;
    }
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  private addLanguageHeader(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const lang = localStorage.getItem('language');
    return request.clone({
      setHeaders: {
        'Accept-Language': (lang && LANGUAGE_MAP[lang]) || DEFAULT_LANGUAGE,
      },
    });
  }

  private handleHttpError(error: HttpErrorResponse): void {
    switch (error.status) {
      case 400:
        this.handle400(error);
        break;
      case 401:
        this.authService.logout(this.authService.resolveSignInRedirectUrl());
        break;
      case 403:
        this.ntf._showNotification('snackbar-danger', 'forbidden');
        break;
      case 404:
        this.ntf._showNotification('snackbar-danger', 'errors.error404');
        break;
      case 500:
        this.ntf._showNotification('snackbar-danger', 'errors.error500');
        break;
      case 502:
      case 504:
        this.handleGatewayError(error);
        break;
      default:
        this.ntf.showNotification('snackbar-danger', error?.error?.error?.message);
        break;
    }
  }

  private handle400(error: HttpErrorResponse): void {
    let message: string = this.translateService.instant('errors.requestError');
    if (Array.isArray(error.error?.details)) {
      message = error.error.details.join('\n');
    } else if (error.error?.message) {
      message = error.error.message;
    }
    this.ntf.showNotification('snackbar-danger', message);
  }

  private handleGatewayError(error: HttpErrorResponse): void {
    const currentUrl = this.router.url;
    const queryParams = this.router.parseUrl(currentUrl).queryParams;
    const isSignin = currentUrl.startsWith('/authentication');
    this.router.navigate(['/page502'], {
      queryParams: {
        from: INTERCEPTOR,
        status: error.status,
        redirectUrl: isSignin ? queryParams['redirectUrl'] : currentUrl,
        msg: error.statusText,
        retryAt: this.parseRetryAt(error.headers?.get('Retry-After')),
      },
    });
  }

  /**
   * Converts an HTTP `Retry-After` header into an absolute epoch (ms) the page502
   * countdown can target. Supports both delta-seconds and HTTP-date forms.
   * Returns `null` when the header is absent or unparseable.
   */
  private parseRetryAt(retryAfter: string | null | undefined): number | null {
    if (!retryAfter) return null;
    const seconds = Number(retryAfter);
    if (!Number.isNaN(seconds)) return Date.now() + seconds * 1000;
    const date = Date.parse(retryAfter);
    return Number.isNaN(date) ? null : date;
  }
}
